import React, { useState, useEffect } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { Ripple } from '../utils/PopupRipple.jsx';
import { formatPriceUptoDecimals } from '../../utils/format';
import './PriceDropAlert.scss';
import { toast } from 'react-toastify';
import { useSymbolContext } from '../../contexts/Symbol-Context';
import { useChartContext } from '../../contexts/Chart-Context';
import { useAuthContext } from '../../contexts/Auth-Context';
import { useMetricsContext } from '../../contexts/Metrics-Context';
import APIMiddleware from '../../data/api/Api-Middleware';
import { API_ENDPOINT_OPEN_POSITION } from '../../data/Endpoints-API';
import { useTranslation } from 'react-i18next';

const PriceDrop = () => {
  const { t } = useTranslation();
  const {
    bidPrice,
    askPrice,
    symbolInfo,
   askPriceExchangeRate,
    bidPriceExchangeRate,
    selectedSymbolExchangeRate,
    setNewPositionOpen,
    lotSize,
    setShowPriceAlert,
    priceAlertDetails,
    setPriceAlertDetails,
    selectedSymbolSession
  } = useSymbolContext();
  const { selectedStyle } = useChartContext();
  const { selectedAuthSymbol, user, platFromData, isFirstLoginDisabled, leverageData, fetchUserLeverage } = useAuthContext();
  const { metrics } = useMetricsContext();
  const [buyActive, setBuyActive] = useState(true);
  const [sellActive, setSellActive] = useState(false);
  const [direction, setDirection] = useState('Buy');
  const [isPositionOpening, setIsPositionOpening] = useState(false);

  const handleBuySellOrderClick = (buy) => {
    setBuyActive(buy);
    setSellActive(!buy);
    setDirection(buy ? 'Buy' : 'Sell');
    localStorage.setItem('directions', buy ? 'buy' : 'sell');
    // setEntryPrice(buy ?   parseFloat(askPrice) : parseFloat(bidPrice));
    // setSecondPrice(buy ?  parseFloat(bidPrice) :parseFloat(askPrice))
  };

  const customStyleforicons = {
    width: '100px',
    // padding: '10px',
    height: '20px',
    fontSize: '18px',
    fontWeight: 700,
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    // backgroundColor: '#1a1f36',
    color: '#e8eaf0', // boldTextColor
    border: '0.1px solid #3a4156', // inputBorderColor
    cursor: 'pointer',
    lineHeight: 1,
    borderRadius: '0px',
    borderTop: '0px',
    backgroundColor: '#2f3549', // firstBoxButton
  };
  const handleIncrement = () => {
    setPriceAlertDetails((prevDetails) => ({
      ...prevDetails,
      quantity:
        parseFloat(prevDetails.quantity) >= 1
          ? parseFloat(prevDetails.quantity) + 1
          : 1,
    }));
  };

  const handleQuantityChange = (e) => {
    const newQuantity =
      e.target.value === '' ? '' : Math.max(1, Number(e.target.value));

    setPriceAlertDetails((prevDetails) => ({
      ...prevDetails,
      quantity: newQuantity,
    }));
  };

  const handleDecrement = () => {
    setPriceAlertDetails((prevDetails) => ({
      ...prevDetails,
      quantity:
        parseFloat(prevDetails.quantity) > 1
          ? parseFloat(prevDetails.quantity) - 1
          : 1,
    }));
  };
  const getEntryPrice = () => (buyActive ? askPrice : bidPrice);
  const getEntryPriceExchangeRate = () => (buyActive ? askPriceExchangeRate : bidPriceExchangeRate);
  
  // Helper function to get leverage for current symbol
  // leverageData is now a single object (not array) for the selected symbol only
  const getCurrentSymbolLeverage = () => {
    if (!leverageData || !selectedAuthSymbol || leverageData.symbol_name !== selectedAuthSymbol) {
      return null;
    }
    return leverageData;
  };

  // Fetch leverage when symbol changes
  useEffect(() => {
    if (user?.userId && selectedAuthSymbol) {
      fetchUserLeverage(user.userId, selectedAuthSymbol);
    }
  }, [selectedAuthSymbol, user?.userId]);

  const getMinimumLeverage = (userLeverage, groupLeverage, symbolLeverage) => {
    const parsedUserLeverage = parseFloat(userLeverage);
    const parsedGroupLeverage = parseFloat(groupLeverage);
    const parsedSymbolLeverage = parseFloat(symbolLeverage);
    if (
      groupLeverage !== undefined &&
      groupLeverage !== null &&
      groupLeverage !== 'null' &&
      !isNaN(parsedGroupLeverage)
    ) {
      return Math.min(parsedUserLeverage, parsedGroupLeverage);
    }

    if (
      symbolLeverage !== undefined &&
      symbolLeverage !== null &&
      symbolLeverage !== 'null' &&
      !isNaN(parsedSymbolLeverage)
    ) {
      return Math.min(parsedUserLeverage, parsedSymbolLeverage);
    }

    return isNaN(parsedUserLeverage) ? 1 : parsedUserLeverage;
  };
  const calculateRequiredMargin = () => {
    let reqMarginWithoutLeverage =
      priceAlertDetails.quantity * getEntryPrice() * selectedSymbolExchangeRate* getEntryPriceExchangeRate();
    const currentLeverage = getCurrentSymbolLeverage();
    if (
      currentLeverage &&
      Array.isArray(currentLeverage.group_level_leverage) &&
      currentLeverage.group_level_leverage.length > 0
    ) {
      let symbolLeverage =
        currentLeverage.group_level_leverage.sort(
          (a, b) => {
            const aLevel =
              a && a.exposure_level !== null && a.exposure_level !== undefined
                ? parseFloat(a.exposure_level)
                : Infinity;
            const bLevel =
              b && b.exposure_level !== null && b.exposure_level !== undefined
                ? parseFloat(b.exposure_level)
                : Infinity;
            return aLevel - bLevel;
          },
        );

      let groupMinLeverage = symbolLeverage.filter((lev) => {
        const exposureLevel =
          lev && lev.exposure_level !== null && lev.exposure_level !== undefined
            ? parseFloat(lev.exposure_level)
            : NaN;
        return (
          !isNaN(exposureLevel) && exposureLevel >= reqMarginWithoutLeverage
        );
      });

      let groupDefaultLeverage;

      if (
        currentLeverage.group_level_leverage.length > 0 &&
        groupMinLeverage.length == 0
      ) {
        groupDefaultLeverage = symbolLeverage[symbolLeverage.length - 1];
      } else {
        groupDefaultLeverage = groupMinLeverage[0];
      }

      let minimumLeverage = 1;
      if (currentLeverage) {
        minimumLeverage = getMinimumLeverage(
          currentLeverage.user_default_leverage,
          groupDefaultLeverage.max_leverage,
          [],
        );
      }
      const lvg = minimumLeverage || 1;

      const converted_entry_price =
        getEntryPrice() * selectedSymbolExchangeRate* getEntryPriceExchangeRate();
      const requiredMargin =
        priceAlertDetails.quantity * (converted_entry_price / lvg);

      return isNaN(requiredMargin)
        ? 0
        : requiredMargin < 1
          ? requiredMargin.toFixed(5)
          : Math.round(requiredMargin * 10) / 10;
    } else if (
      currentLeverage &&
      Array.isArray(currentLeverage.symbol_default_leverage) &&
      currentLeverage.symbol_default_leverage.length > 0
    ) {
      let symbolLeverage =
        currentLeverage.symbol_default_leverage.sort(
          (a, b) => {
            const aLevel =
              a && a.exposure_level !== null && a.exposure_level !== undefined
                ? parseFloat(a.exposure_level)
                : Infinity;
            const bLevel =
              b && b.exposure_level !== null && b.exposure_level !== undefined
                ? parseFloat(b.exposure_level)
                : Infinity;
            return aLevel - bLevel;
          },
        );

      let symbolMinLeverage = symbolLeverage.filter((lev) => {
        const exposureLevel =
          lev && lev.exposure_level !== null && lev.exposure_level !== undefined
            ? parseFloat(lev.exposure_level)
            : NaN;
        return (
          !isNaN(exposureLevel) && exposureLevel >= reqMarginWithoutLeverage
        );
      });

      let symbolDefaultLeverage;

      if (
        currentLeverage.symbol_default_leverage.length > 0 &&
        symbolMinLeverage.length == 0
      ) {
        symbolDefaultLeverage = symbolLeverage[symbolLeverage.length - 1];
      } else {
        symbolDefaultLeverage = symbolMinLeverage[0];
      }

      let minimumLeverage = 1;
      if (currentLeverage) {
        minimumLeverage = getMinimumLeverage(
          currentLeverage.user_default_leverage,
          [],
          symbolDefaultLeverage.max_leverage,
        );
      }
      const lvg = minimumLeverage || 1;
      const converted_entry_price =
        getEntryPrice() * selectedSymbolExchangeRate* getEntryPriceExchangeRate();
      const requiredMargin =
        priceAlertDetails.quantity * (converted_entry_price / lvg);
      return isNaN(requiredMargin)
        ? 0
        : requiredMargin < 1
          ? requiredMargin.toFixed(5)
          : Math.round(requiredMargin * 10) / 10;
    } else {
      const currentLeverage = getCurrentSymbolLeverage();
      if (currentLeverage && currentLeverage.user_default_leverage) {
        const lvg = currentLeverage.user_default_leverage || 1;
      const converted_entry_price =
        getEntryPrice() * selectedSymbolExchangeRate* getEntryPriceExchangeRate();
      const requiredMargin =
        priceAlertDetails.quantity * (converted_entry_price / lvg);

      return isNaN(requiredMargin)
        ? 0
        : requiredMargin < 1
          ? requiredMargin.toFixed(5)
          : Math.round(requiredMargin * 10) / 10;
      }
    }
    // Fallback if no leverage data available
    return 0;
  };
  function calculateNetMarginInti(allOpenedPositions, requiredMargin) {
    let positions = [
      ...allOpenedPositions.openedPositions,
      { symbol: selectedAuthSymbol, direction, margin: requiredMargin },
    ];

    const positionsBySymbol = positions.reduce((acc, position) => {
      if (!position || !position.symbol || !position.direction) {
        return acc;
      }
  
      const symbol = position.symbol;
      const direction = position.direction;
      const margin = parseFloat(position.margin || 0);

      if (!acc[symbol]) {
        acc[symbol] = { Buy: 0, Sell: 0 };
      }

      if (direction === 'Buy') {
        acc[symbol].Buy += margin;
      } else if (direction === 'Sell') {
        acc[symbol].Sell += margin;
      }
      return acc;
    }, {});

    let totalMargin = 0;

    for (const symbol in positionsBySymbol) {
      const { Buy, Sell } = positionsBySymbol[symbol];
      if (Buy > Sell) {
        totalMargin += Buy - Sell;
      } else {
        totalMargin += Sell - Buy;
      }
    }
    return totalMargin;
  }
  const placeOrder = () => {
    if (user && user.userId != undefined && user.userId > 0) {
      setIsPositionOpening(true);
      placeMarketOrder();
    }
  };
  const placeMarketOrder = () => {
    if ((buyActive || sellActive) && priceAlertDetails.quantity > 0) {
      const requiredMargin = calculateRequiredMargin();

      let useMargin = calculateNetMarginInti(platFromData[3], requiredMargin);
      let freeMargin;
      let openPositionCheck;

      if (platFromData[5].margin_calculation == 'net') {
        freeMargin = platFromData[5].equity - useMargin;

        openPositionCheck = freeMargin > 0;
      } else {
        freeMargin = metrics.freeMargin;
        openPositionCheck = freeMargin >= requiredMargin;
      }
      // Check if the user has enough balance
      if (openPositionCheck) {
        // Continue with opening the position
        openPosition_api(requiredMargin);
      } else {
        // Show an alert for insufficient balance
        toast.error('Insufficient balance to open the position.', {
          position: 'top-right',
        });
        setIsPositionOpening(false);
        document.getElementById('closeSound').play();
        document.querySelector('.deposit-cash')?.setAttribute('view', 'true');
        const ig = document.getElementById(
          'priceAlertDetails.quantity-input-guide',
        );
        ig?.setAttribute('shake', 'true');
        setTimeout(() => {
          ig?.removeAttribute('shake');
        }, 2000);
      }
    } else {
      toast.error('Quantity should be greater than 0.', {
        position: 'top-right',
      });
      document.getElementById('closeSound').play();
      setIsPositionOpening(false);
    }
  };

  const openPosition_api = async (requiredMargin) => {
        if(requiredMargin < 10){
          toast.error(`Margin Should be greater than 10 ${metrics?.userCurrencyName || 'EUR'}.`, {
            position: "top-right",
          });
          setIsPositionOpening(false);
          document.getElementById("closeSound").play();
          return;
        }
    try {
      const data = {
        id: -1,
        position_id: 'PID' + Math.floor(100000 + Math.random() * 900000),
        symbol: selectedAuthSymbol,
        quantity: priceAlertDetails.quantity,
        amount: 0,
        asset_type: 'base_asset',
        direction: direction,
        entry_price: getEntryPrice(),
        TP: 0,
        SL: 0,
        netEUR: 0,
        status: '',
        userId: user.userId,
        exit_price: 0,
        totalUnrealizedPnL: metrics.totalUnrealizedPnL,
        position_closed_at: null,
        comment: '',
        stop_loss_pips: 0,
        take_profit_pips: 0,
        lot_step: 0,
        trade_type: symbolInfo.trade_type,
        lot_size: lotSize,
      };
      const response = await APIMiddleware.post(
        API_ENDPOINT_OPEN_POSITION(),
        data,
      );
      const data_m = {
        ...response.data,
        created_at: new Date(response.data.created_at).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      };

      toast.success('Position opened successfully!', { position: 'top-right' });
      setIsPositionOpening(false);
      localStorage.accountManager = 'open-positions-acc';
      document.getElementById('openSound').play();
      setNewPositionOpen(Math.floor(Math.random() * 100));
    } catch (error) {
      console.error(`API request error: ${API_ENDPOINT_OPEN_POSITION()}`, error);
      setIsPositionOpening(false);
    } finally {
      hideModal();
      setIsPositionOpening(false);
    }
  };
  const hideModal = () => {
    setShowPriceAlert(false);
  };
  console.log(priceAlertDetails.readOnly)
  return (
    <div className="confirm-boxs" role="true">
      <div className="card width">
        <h2>{priceAlertDetails?.title || ''}</h2>
        <div
          className='price-div'
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 40px',
          }}
        >
          <div className='price-first-div'>
          <div className="color-gray symbol-no-wrap">{priceAlertDetails?.symbol || ''}</div>
          <div className="amount-div width200">
              <input
                type="number"
                className="amount-input"
                disabled={priceAlertDetails.readOnly}
                value={priceAlertDetails.quantity}
                onChange={handleQuantityChange}
                placeholder={t('mobileTrading.quantity')}
                style={{
                  width: '100%',
                  padding: '5px',
                  height: '30px',
                  textAlign: 'center',
                  borderRadius: '0px',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', width : '100%' }} >
                <button
                  // style={{width : '100px !important'}}
                  disabled={priceAlertDetails.readOnly}
                  style={customStyleforicons}
                  onClick={() => handleDecrement('priceAlertDetails.quantity')}
                >
                  -
                </button>
                <button
                  // style={{width : '100px !important'}}
                  disabled={priceAlertDetails.readOnly}
                  style={customStyleforicons}
                  onClick={() => handleIncrement('priceAlertDetails.quantity')}
                >
                  +
                </button>
              </div>
            </div>
          </div>


          <div className="price-box-container">
            <div
              className={`price-box price-box-buy ${
                buyActive ? 'price-box-buy-active' : ''
              }`}
              id="buyMarketOrder"
              onClick={() => handleBuySellOrderClick(true)}
            >
              <div className='buy-small-font' style={{ fontSize: '14px', fontWeight: 'bolder' }}>{t('marketOrder.buy')}</div>
              <div id="buyMarketPrice" style={{ fontSize: '12px' }}>
                { askPrice }
              </div>
            </div>
            <div
              className={`price-box price-box-sell ${
                sellActive ? 'price-box-sell-active' : ''
              }`}
              id="sellMarketOrder"
              onClick={() => handleBuySellOrderClick(false)}
            >
              <div className='sell-small-font' style={{ fontSize: '14px', fontWeight: 'bolder', paddingRight: '5px' }}>{t('marketOrder.sell')}</div>
              <div id="sellMarketPrice" style={{ fontSize: '12px' }}>
                { bidPrice }
              </div>
            </div>
          </div>
          <div>
          </div>
        </div>
        <hr />
        <div className="px-40 py-10 color-gray description-full-width">
          {priceAlertDetails?.description || ''}
        </div>
        <div className="row-scss">
          <button id='cancel-button' onClick={hideModal} style={{backgroundColor: selectedStyle.sellColor}}>
            <Ripple />
            {t('common.cancel')}
          </button>
          <div className="blank"></div>
          <button
            id='trade-button'
            onClick={placeOrder}
            disabled={isFirstLoginDisabled || isPositionOpening || (platFromData[5]?.riskLocked || platFromData[5]?.accessRight == 3) || localStorage.getItem('accountType') == "0" || platFromData[5]?.accessRight == 2 || !bidPrice || !askPrice || selectedSymbolSession === 0}
            style={{
              position: 'relative',
              overflow: 'hidden',
              isolation: 'isolate',
              backgroundColor: buyActive ? selectedStyle.buyColor : selectedStyle.sellColor,
              color: '#1a1f36',
            }}
          >
            <Ripple />
            {isPositionOpening ? t('marketOrder.loading') : (buyActive ? t('marketOrder.buyNow') : t('marketOrder.sellNow'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceDrop;
